import { useUserInfo } from "@/api/user";
import { config } from "@/config";
import { LDContext, LDProvider } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

export const flagValues = {
  onboarding: "onboarding",
  // LD key: new-home. The home page's one read; absent means on, and
  // targeting turns it off for the organizations that keep the legacy home.
  newHome: "newHome",
  // LD key: first-run. The new-user first run; absent means off. Registered
  // only: the dev override (`?firstRun=1`) is the test path.
  firstRun: "firstRun",
  notionLikeEditor: "notionLikeEditor",
  maintenanceMode: "maintenanceMode",
  extremeReasoningEffort: "extremeReasoningEffort",
  // LD key: notifications — per-org gate for the Liveblocks notifications feature
  // (the bell + tool-confirmation auto-delete). Matches the backend flag key.
  notifications: "notifications",
  // LD key: harness-runtime — the same gate the backend spends at session create. The
  // model-provider surface hangs off it because a credential connected while turns still run
  // through the ChatCompletion loop is one nothing would ever spend, and so does the composer's
  // harness selector: off, Cohesive is the only choice and there is nothing to select.
  harnessRuntime: "harnessRuntime",
  // LD key: canvas-toolbar-redraw — the redrawn primary canvas toolbar (the
  // matted bar, the tray, the New Chat circle). Off or absent is the toolbar
  // as it was on main @ 155b7ca01 (DECISIONS D30). Read at the canvas page's
  // mount points only.
  canvasToolbarRedraw: "canvasToolbarRedraw",
  // LD key: coachmarks — per-org/user gate for the coachmarks that fire at
  // first-use moments after onboarding. Absent is off.
  coachmarks: "coachmarks",
} as const;

export type Flags = {
  [K in keyof typeof flagValues]: boolean;
};

export function LaunchDarklyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: userInfo } = useUserInfo();

  const launchDarklyContext = useMemo(() => {
    if (userInfo) {
      const context: LDContext = {
        kind: "multi",
        user: {
          key: userInfo?.user?.id,
          name: userInfo?.user?.name,
          email: userInfo?.user?.email,
          created_at: userInfo?.user?.created_at,
        },
      };

      if (userInfo?.organization) {
        context.organization = {
          key: userInfo?.organization?.id,
          name: userInfo?.organization?.name,
        };
      }

      return context;
    }

    return undefined;
  }, [userInfo]);

  const relayHost = config.launchDarkly.relayHost;

  return (
    <LDProvider
      clientSideID={config.launchDarkly.clientSideId}
      context={launchDarklyContext}
      deferInitialization
      options={
        relayHost
          ? {
              baseUrl: relayHost,
              streamUrl: relayHost,
              eventsUrl: relayHost,
            }
          : undefined
      }
    >
      {children}
    </LDProvider>
  );
}
